import React, { useState, useEffect, createContext, useContext } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Context for authentication and cart
const AppContext = createContext();

export const useAppContext = () => {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error('useAppContext must be used within AppProvider');
    }
    return context;
};

// AppProvider component
export const AppProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [cart, setCart] = useState([]);
    const [wishlist, setWishlist] = useState([]);
    const [loading, setLoading] = useState(false);

    // Load user from localStorage
    useEffect(() => {
        const token = localStorage.getItem('token');
        const userData = localStorage.getItem('user');
        if (token && userData) {
            setUser(JSON.parse(userData));
            fetchCart();
        }
    }, []);

    const fetchCart = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;

            const response = await axios.get(`${API}/cart`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCart(response.data);
        } catch (error) {
            if (error.response && error.response.status === 401) {
                // Token expired or invalid, just clear user state silently or handle refresh
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                setUser(null);
            } else {
                console.error('Failed to fetch cart:', error);
            }
        }
    };

    const login = async (email, password) => {
        try {
            setLoading(true);
            const response = await axios.post(`${API}/auth/login`, { email, password });
            const { access_token, user } = response.data;

            localStorage.setItem('token', access_token);
            localStorage.setItem('user', JSON.stringify(user));
            setUser(user);

            toast.success('Login successful!');
            await fetchCart();
            return true;
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Login failed');
            return false;
        } finally {
            setLoading(false);
        }
    };

    const register = async (userData) => {
        try {
            setLoading(true);
            const response = await axios.post(`${API}/auth/register`, userData);
            const { access_token, user } = response.data;

            localStorage.setItem('token', access_token);
            localStorage.setItem('user', JSON.stringify(user));
            setUser(user);

            toast.success('Registration successful!');
            return true;
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Registration failed');
            return false;
        } finally {
            setLoading(false);
        }
    };

    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        setCart([]);
        setWishlist([]);
        toast.success('Logged out successfully');
    };

    const addToCart = async (productId, quantity = 1, size = null, designedPages = null) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                toast.error('Please login to add items to cart');
                return;
            }

            // If we have designed pages, upload them to S3 first
            let uploadedDesigns = [];
            if (designedPages && designedPages.length > 0) {
                toast.loading('Uploading your designs...', { id: 'upload-designs' });

                try {
                    // Upload each designed page to S3 using presigned URL
                    const uploadPromises = designedPages.map(async (design) => {
                        const response = await fetch(design.imageData);
                        const blob = await response.blob();
                        const filename = `custom-temp-${Date.now()}-${design.pageName.replace(/\s+/g, '-')}.png`;

                        // 1. Get presigned URL
                        const presignResponse = await axios.post(`${API}/generate-upload-url`, {
                            filename: filename,
                            contentType: 'image/png',
                            folder: 'custom-designs'
                        }, {
                            headers: { 'Authorization': `Bearer ${token}` }
                        });

                        const { uploadUrl, imageUrl } = presignResponse.data;

                        // 2. Upload directly to S3 using fetch to ensure absolutely no unwanted headers
                        const uploadResult = await fetch(uploadUrl, {
                            method: 'PUT',
                            body: blob,
                            headers: {
                                'Content-Type': 'image/png'
                            }
                        });

                        if (!uploadResult.ok) {
                            throw new Error(`S3 upload failed: ${uploadResult.status}`);
                        }

                        return {
                            pageName: design.pageName,
                            pageIndex: design.pageIndex,
                            imageUrl: imageUrl
                        };
                    });

                    uploadedDesigns = await Promise.all(uploadPromises);
                    toast.success('Designs uploaded!', { id: 'upload-designs' });
                } catch (uploadError) {
                    console.error('Failed to upload designs:', uploadError);
                    toast.error('Failed to upload designs. Please try again.', { id: 'upload-designs' });
                    return;
                }
            }

            const formData = new FormData();
            formData.append('product_id', productId);
            formData.append('quantity', quantity);
            if (size) formData.append('size', size);

            const response = await axios.post(`${API}/cart/items`, formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            // Store the S3 URLs (much smaller than base64 images)
            if (uploadedDesigns.length > 0) {
                const cartItemId = response.data.id;
                const existingDesigns = JSON.parse(localStorage.getItem('customDesigns') || '{}');
                existingDesigns[cartItemId] = uploadedDesigns;
                localStorage.setItem('customDesigns', JSON.stringify(existingDesigns));
            }

            await fetchCart();
            toast.success('Item added to cart!');
        } catch (error) {
            console.error('Add to cart error:', error);
            toast.error('Failed to add item to cart');
        }
    };

    const removeFromCart = async (itemId) => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API}/cart/${itemId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            await fetchCart();
            toast.success('Item removed from cart');
        } catch (error) {
            toast.error('Failed to remove item');
        }
    };

    const updateCartItemQuantity = async (itemId, newQuantity) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;
            await axios.put(
                `${API}/cart/${itemId}?quantity=${newQuantity}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            await fetchCart();
        } catch (error) {
            toast.error('Failed to update quantity');
        }
    };

    const value = {
        user,
        cart,
        wishlist,
        loading,
        login,
        register,
        logout,
        addToCart,
        removeFromCart,
        fetchCart,
        updateCartItemQuantity
    };

    return (
        <AppContext.Provider value={value}>
            {children}
        </AppContext.Provider>
    );
};
